/**
 * original source: https://ui.shadcn.com/docs/components/sidebar
 */
import { createPortal } from 'react-dom'
import {
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  PanelRightCloseIcon,
  PanelRightOpenIcon,
  XIcon,
} from 'lucide-react'
import { IconButton } from './Button'
import { useSidebarResize } from '../hooks/useSidebarResize'
import { VisuallyHidden } from './VisuallyHidden'
import { useIsMobile } from '../hooks/useIsMobile'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './Sheet'

import { cn, createNonNullableContext, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { useMergeRefs } from 'react-merge-refs'
import type { ComponentProps, CSSProperties, ReactNode } from 'react'
import { createContext, use, useEffect, useRef, useState } from 'react'

const sidebarSeparatorVariants = tv({
  base: 'bg-sidebar-border h-px w-auto shrink-0',

  defaultVariants: {
    inset: 'gutter',
  },

  variants: {
    inset: {
      flush: 'mx-0',
      gutter: 'mx-2',
    },
  },
})

const MIN_SIDEBAR_WIDTH = '22rem'
const MAX_SIDEBAR_WIDTH = '60rem'
/**
 * Minimum width reserved for the app shell (nav + main) when the sidebar is
 * expanded. Prevents the fixed sidebar from collapsing the shell so far that
 * navbar items overlap.
 */
const MIN_REMAINING_LAYOUT_WIDTH = '40rem'
const SIDEBAR_WIDTH = '28rem'
const SIDEBAR_WIDTH_MOBILE = '22rem'
const SIDEBAR_WIDTH_ICON = '3rem'

function parseCssLengthToPixels(width: string): number | null {
  const trimmedWidth = width.trim()
  if (trimmedWidth.endsWith('rem')) {
    const value = Number.parseFloat(trimmedWidth.slice(0, -3))
    return Number.isFinite(value) ? value * 16 : null
  }

  if (trimmedWidth.endsWith('px')) {
    const value = Number.parseFloat(trimmedWidth.slice(0, -2))
    return Number.isFinite(value) ? value : null
  }

  return null
}

function sanitizeSidebarWidth(width: string, fallback: string): string {
  return parseCssLengthToPixels(width) === null ? fallback : width
}

function formatRemWidthFromPixels(widthPixels: number): string {
  return `${(widthPixels / 16).toFixed(1)}rem`
}

function getViewportClampedMaxSidebarWidth(absoluteMaxWidth: string): string {
  if (typeof window === 'undefined') {
    return absoluteMaxWidth
  }

  const absoluteMaxWidthPixels = parseCssLengthToPixels(absoluteMaxWidth)
  const reservedLayoutWidthPixels = parseCssLengthToPixels(
    MIN_REMAINING_LAYOUT_WIDTH,
  )
  const minSidebarWidthPixels = parseCssLengthToPixels(MIN_SIDEBAR_WIDTH)
  if (
    absoluteMaxWidthPixels === null ||
    reservedLayoutWidthPixels === null ||
    minSidebarWidthPixels === null
  ) {
    return absoluteMaxWidth
  }

  const viewportMaxWidthPixels = Math.max(
    minSidebarWidthPixels,
    window.innerWidth - reservedLayoutWidthPixels,
  )
  return formatRemWidthFromPixels(
    Math.min(absoluteMaxWidthPixels, viewportMaxWidthPixels),
  )
}

function clampSidebarWidth({
  fallbackWidth,
  maxWidth,
  minWidth,
  width,
}: {
  fallbackWidth: string
  maxWidth: string
  minWidth: string
  width: string
}): string {
  const widthPixels =
    parseCssLengthToPixels(width) ??
    parseCssLengthToPixels(fallbackWidth) ??
    parseCssLengthToPixels(SIDEBAR_WIDTH)
  const minWidthPixels =
    parseCssLengthToPixels(minWidth) ??
    parseCssLengthToPixels(MIN_SIDEBAR_WIDTH)
  const maxWidthPixels =
    parseCssLengthToPixels(maxWidth) ??
    parseCssLengthToPixels(MAX_SIDEBAR_WIDTH)
  if (
    widthPixels === null ||
    minWidthPixels === null ||
    maxWidthPixels === null
  ) {
    return fallbackWidth
  }

  const clampedWidthPixels = Math.min(
    maxWidthPixels,
    Math.max(minWidthPixels, widthPixels),
  )
  return formatRemWidthFromPixels(clampedWidthPixels)
}

interface SidebarContextProps {
  isDraggingRail: boolean
  isMobile: boolean
  maxWidth: string
  open: boolean
  openMobile: boolean
  setDraggingRail: (isDragging: boolean) => void
  setOpen: (open: boolean | ((currentOpen: boolean) => boolean)) => void
  setOpenMobile: (open: boolean | ((currentOpen: boolean) => boolean)) => void
  setWidth: (width: string) => void
  state: 'expanded' | 'collapsed'
  toggleSidebar: () => void
  width: string
  widthLocalStorageKey: string
}

const SidebarWidthLocalStorageKey = 'sidebar:width'

const SidebarContext =
  createNonNullableContext<SidebarContextProps>('SidebarContext')

const useSidebar = SidebarContext.useRequiredContext

interface SidebarPortalContextValue {
  left: HTMLElement | null
  right: HTMLElement | null
}

const SidebarPortalContext = createContext<SidebarPortalContextValue>({
  left: null,
  right: null,
})

export interface SidebarPortalProviderProps {
  children: ReactNode
}

export function SidebarPortalProvider({
  children,
}: SidebarPortalProviderProps) {
  const [leftPortalContainer, setLeftPortalContainer] =
    useState<HTMLElement | null>(null)
  const [rightPortalContainer, setRightPortalContainer] =
    useState<HTMLElement | null>(null)

  return (
    <SidebarPortalContext
      value={{
        left: leftPortalContainer,
        right: rightPortalContainer,
      }}
    >
      <div ref={setLeftPortalContainer} className="contents" />
      {children}
      <div ref={setRightPortalContainer} className="contents" />
    </SidebarPortalContext>
  )
}

export interface SidebarProviderProps {
  children: ReactNode
  defaultOpen?: boolean
  defaultWidth?: string
  /**
   * Upper bound for the width; the viewport can still lower it.
   * @default MAX_SIDEBAR_WIDTH
   */
  maxWidth?: string
  /**
   * Lower bound for the width, including a width saved in local storage.
   * @default MIN_SIDEBAR_WIDTH
   */
  minWidth?: string
  onOpenChange?: (open: boolean) => void
  open?: boolean

  /**
   * @default SidebarWidthLocalStorageKey
   */
  widthLocalStorageKey?: string
}

function SidebarProvider({
  children,
  defaultOpen = false,
  defaultWidth = SIDEBAR_WIDTH,
  maxWidth: absoluteMaxWidth = MAX_SIDEBAR_WIDTH,
  minWidth = MIN_SIDEBAR_WIDTH,
  onOpenChange: setOpenProp,
  open: openProp,
  widthLocalStorageKey = SidebarWidthLocalStorageKey,
}: SidebarProviderProps) {
  const isMobile = useIsMobile()
  const preferredWidthRef = useRef(
    sanitizeSidebarWidth(
      localStorage.getItem(widthLocalStorageKey) ?? defaultWidth,
      defaultWidth,
    ),
  )
  const [maxWidth, setMaxWidth] = useState(() => {
    return getViewportClampedMaxSidebarWidth(absoluteMaxWidth)
  })
  const maxWidthRef = useRef(maxWidth)
  maxWidthRef.current = maxWidth
  const [width, setWidthState] = useState(() => {
    return clampSidebarWidth({
      fallbackWidth: defaultWidth,
      maxWidth: getViewportClampedMaxSidebarWidth(absoluteMaxWidth),
      minWidth,
      width: preferredWidthRef.current,
    })
  })
  const [isDraggingRail, setDraggingRail] = useState(false)
  const setWidthRef = useRef((nextWidth: string) => {
    const sanitizedWidth = sanitizeSidebarWidth(nextWidth, defaultWidth)
    preferredWidthRef.current = sanitizedWidth
    setWidthState(
      clampSidebarWidth({
        fallbackWidth: defaultWidth,
        maxWidth: maxWidthRef.current,
        minWidth,
        width: sanitizedWidth,
      }),
    )
  })

  useEffect(() => {
    const syncSidebarBoundsToViewport = () => {
      const nextMaxWidth = getViewportClampedMaxSidebarWidth(absoluteMaxWidth)
      setMaxWidth(nextMaxWidth)
      setWidthState(
        clampSidebarWidth({
          fallbackWidth: defaultWidth,
          maxWidth: nextMaxWidth,
          minWidth,
          width: preferredWidthRef.current,
        }),
      )
    }

    window.addEventListener('resize', syncSidebarBoundsToViewport)
    return () => {
      window.removeEventListener('resize', syncSidebarBoundsToViewport)
    }
  }, [absoluteMaxWidth, defaultWidth, minWidth])

  const [_open, _setOpen] = useState(defaultOpen)
  const open = openProp ?? _open
  const setOpen = (value: boolean | ((currentOpen: boolean) => boolean)) => {
    const openState = typeof value === 'function' ? value(open) : value
    if (setOpenProp) {
      setOpenProp(openState)
    } else {
      _setOpen(openState)
    }
  }

  const toggleSidebar = () => {
    setOpen((currentOpen) => {
      return !currentOpen
    })
  }

  const state = open ? 'expanded' : 'collapsed'

  const contextValue: SidebarContextProps = {
    isDraggingRail,
    isMobile,
    maxWidth,
    open,
    openMobile: open,
    setDraggingRail,
    setOpen,
    setOpenMobile: setOpen,
    setWidth: setWidthRef.current,
    state,
    toggleSidebar,
    width,
    widthLocalStorageKey,
  }

  return (
    <SidebarContext.Context value={contextValue}>
      {children}
    </SidebarContext.Context>
  )
}

export type SidebarPosition = 'fixed' | 'inline'

interface SidebarRailContextProps {
  minWidth: string
  position: SidebarPosition
  side: 'left' | 'right'
}

const SidebarRailContext =
  createNonNullableContext<SidebarRailContextProps>('SidebarRailContext')

function Sidebar({
  children,
  className,
  collapsible = 'offcanvas',
  minWidth = MIN_SIDEBAR_WIDTH,
  position = 'fixed',
  ref,
  side = 'left',
  variant = 'sidebar',
  ...props
}: ComponentProps<'div'> & {
  collapsible?: 'offcanvas' | 'icon' | 'none'
  minWidth?: string
  position?: SidebarPosition
  side?: 'left' | 'right'
  variant?: 'sidebar' | 'floating' | 'inset'
}) {
  const { isDraggingRail, isMobile, open, setOpen, state, width } =
    SidebarContext.useRequiredContext()
  const portalContainer = use(SidebarPortalContext)[side]

  if (portalContainer === null) {
    return null
  }

  const sidebarCssVars: CSSProperties = {
    '--sidebar-width': width,
    '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
  }
  const railContextValue: SidebarRailContextProps = {
    minWidth,
    position,
    side,
  }

  let sidebarContent: ReactNode = (
    <div
      ref={ref}
      className={cn(
        'group peer text-sidebar-foreground hidden md:block',
        position === 'inline' && 'relative overflow-hidden',
      )}
      style={sidebarCssVars}
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-dragging={isDraggingRail}
    >
      <div
        className={cn(
          'relative w-(--sidebar-width) bg-transparent transition-[width] duration-150 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
          'group-data-[dragging=true]_*:duration-0! group-data-[dragging=true]:duration-0!',
        )}
      />
      <div
        className={cn(
          'z-10 flex w-(--sidebar-width) transition-[left,right,width] duration-150 ease-linear',
          position === 'inline'
            ? 'absolute inset-y-0 h-full'
            : 'fixed inset-y-0 h-svh',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 shadow-[-5px_0_10px_-5px_rgba(0,0,0,0.1)] group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
          'group-data-[dragging=true]_*:duration-0! group-data-[dragging=true]:duration-0!',
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex size-full flex-col overflow-hidden group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  )

  if (collapsible === 'none') {
    sidebarContent = (
      <div
        className={cn(
          'bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col',
          className,
        )}
        style={sidebarCssVars}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    )
  } else if (isMobile === true) {
    sidebarContent = (
      <Sheet open={open} onOpenChange={setOpen} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden"
          style={{
            '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
          }}
          side={side}
        >
          <VisuallyHidden asChild>
            <SheetHeader>
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
          </VisuallyHidden>
          <div className="flex size-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return createPortal(
    <SidebarRailContext.Context value={railContextValue}>
      {sidebarContent}
    </SidebarRailContext.Context>,
    portalContainer,
  )
}

type SidebarRailProps = ComponentProps<'button'>

function SidebarRail(props: SidebarRailProps) {
  const { isMobile } = SidebarContext.useRequiredContext()

  if (isMobile) {
    return null
  }

  return <SidebarRailButton {...props} />
}

function SidebarRailButton({ className, ref, ...props }: SidebarRailProps) {
  const {
    maxWidth,
    setDraggingRail,
    setWidth,
    state,
    toggleSidebar,
    width,
    widthLocalStorageKey,
  } = SidebarContext.useRequiredContext()
  const { minWidth, position, side } = SidebarRailContext.useRequiredContext()

  const { dragRef, handleMouseDown } = useSidebarResize({
    currentWidth: width,
    direction: side === 'left' ? 'right' : 'left',
    enableDrag: true,
    isCollapsed: state === 'collapsed',
    isNested: position === 'inline',
    maxResizeWidth: maxWidth,
    minResizeWidth: minWidth,
    onResize: setWidth,
    onToggle: toggleSidebar,
    setDraggingRail,
    widthLocalStorageKey,
  })
  const finalRef = useMergeRefs([ref, dragRef])

  return (
    <button
      type="button"
      ref={finalRef}
      data-sidebar="rail"
      aria-label="Toggle sidebar"
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      title="Toggle sidebar"
      className={cn(
        'hover:after:bg-sidebar-border group-data-[dragging=true]:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 cursor-ew-resize transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] group-data-[dragging=true]:after:w-[3px] group-data-[side=left]:after:-translate-x-full sm:flex',
        'group-data-[collapsible=offcanvas]:hover:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  )
}

function SidebarInset({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'bg-tremor-background relative flex min-w-0 flex-1 flex-col',
        'md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2',
        // 'min-w-5xl',
        className,
      )}
      {...props}
    />
  )
}

function SidebarHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  )
}

function SidebarCloseIconButton({
  onClick,
  ...props
}: Omit<ComponentProps<typeof IconButton>, 'children'>) {
  const { setOpen } = SidebarContext.useRequiredContext()

  return (
    <IconButton
      tooltip="Close"
      variant="ghost"
      {...props}
      onClick={(event) => {
        onClick?.(event)
        if (event.defaultPrevented) {
          return
        }

        setOpen(false)
      }}
    >
      <XIcon className="stroke-2" />
    </IconButton>
  )
}

function SidebarSeparator({
  inset,
  ...props
}: Omit<ComponentProps<'div'>, 'className'> &
  VariantProps<typeof sidebarSeparatorVariants>) {
  return (
    <div
      data-sidebar="separator"
      className={sidebarSeparatorVariants({ inset })}
      {...props}
    />
  )
}

const sidebarContentVariants = tv({
  base: 'flex min-h-0 flex-[1_0_0] flex-col group-data-[collapsible=icon]:overflow-hidden',

  defaultVariants: {
    background: 'default',
    gap: 'md',
    overflow: 'scroll',
  },

  variants: {
    background: {
      default: null,
      muted: 'bg-tremor-background-muted',
    },
    gap: {
      md: 'gap-2',
      none: 'gap-0',
    },
    overflow: {
      hidden: 'overflow-hidden',
      scroll: 'scrollable-y',
    },

    scrollAnchor: {
      end: 'flex-col-reverse',
    },
  },
})

export interface SidebarContentProps
  extends ComponentProps<'div'>, VariantProps<typeof sidebarContentVariants> {}

function SidebarContent({
  background,
  className,
  gap,
  overflow,
  scrollAnchor,
  ...props
}: SidebarContentProps) {
  return (
    <div
      data-sidebar="content"
      className={cn(
        sidebarContentVariants({ background, gap, overflow, scrollAnchor }),
        className,
      )}
      {...props}
    />
  )
}

export interface SidebarToggleIconButtonProps {
  isOpen: boolean
  onToggle: () => void
  side?: 'left' | 'right'
  size?: 'md' | 'sm'
}

function SidebarToggleIconButton({
  isOpen,
  onToggle,
  side = 'right',
  size = 'sm',
}: SidebarToggleIconButtonProps) {
  const ClosedIcon = side === 'left' ? PanelLeftCloseIcon : PanelRightCloseIcon
  const OpenedIcon = side === 'left' ? PanelLeftOpenIcon : PanelRightOpenIcon

  return (
    <IconButton
      tooltip={isOpen ? 'Close sidebar' : 'Open sidebar'}
      variant="ghost"
      size={size}
      onClick={() => {
        onToggle()
      }}
    >
      {isOpen ? <ClosedIcon /> : <OpenedIcon />}
    </IconButton>
  )
}

export {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarCloseIconButton,
  SidebarToggleIconButton,
  useSidebar,
}
